
import { initializeApp, getApps, getApp, type FirebaseApp, FirebaseError } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";

const apiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;
const authDomain = process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN;
const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
const storageBucket = process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET;
const messagingSenderId = process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID;
const appIdEnv = process.env.NEXT_PUBLIC_FIREBASE_APP_ID;

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let initializationError: string | null = null;

if (!apiKey) {
  initializationError = "Firebase API Key (NEXT_PUBLIC_FIREBASE_API_KEY) is not defined or is empty. Please set it up in your .env.local file or your deployment environment's settings.";
  console.error(`[Firebase Client Error] ${initializationError}`);
} else if (!authDomain) {
  initializationError = "Firebase Auth Domain (NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN) is not defined. Firebase initialization aborted.";
  console.warn(`[Firebase Client Warning] ${initializationError}`);
} else if (!projectId) {
  initializationError = "Firebase Project ID (NEXT_PUBLIC_FIREBASE_PROJECT_ID) is not defined. Firebase initialization aborted.";
  console.warn(`[Firebase Client Warning] ${initializationError}`);
}

if (!initializationError) {
  const firebaseConfig = {
    apiKey: apiKey,
    authDomain: authDomain,
    projectId: projectId,
    storageBucket: storageBucket,
    messagingSenderId: messagingSenderId,
    appId: appIdEnv,
  };

  try {
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApp();
    }
  } catch (e) {
    let errorMessage = "Firebase app initialization failed.";
    if (e instanceof FirebaseError) {
      errorMessage += ` Code: ${e.code}. Message: ${e.message}.`;
    } else if (e instanceof Error) {
      errorMessage += ` Error: ${e.message}.`;
    } else {
      errorMessage += ` Unexpected error: ${String(e)}.`;
    }
    initializationError = errorMessage;
    console.error(`[Firebase Client Error] ${initializationError}`, e);
    app = null; // Ensure app is null on error
  }

  if (app && typeof window !== 'undefined') { // Initialize auth only on the client and if app is valid
    try {
      auth = getAuth(app);
    } catch (e) {
      let authErrorMessage = "Firebase getAuth() failed on client.";
      if (e instanceof FirebaseError) {
        authErrorMessage += ` Code: ${e.code}. Message: ${e.message}.`;
      } else if (e instanceof Error) {
        authErrorMessage += ` Error: ${e.message}.`;
      } else {
        authErrorMessage += ` Unexpected error: ${String(e)}.`;
      }
      initializationError = initializationError ? `${initializationError}\n${authErrorMessage}` : authErrorMessage;
      console.error(`[Firebase Client Error] ${authErrorMessage}`, e);
      auth = null; // Ensure auth is null on error
    }
  }
}

export const getFirebaseInitializationError = (): string | null => initializationError;

export { app, auth };
